import React, { useContext, useMemo } from 'react';
import { Box, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import OpenInNew from '@mui/icons-material/OpenInNew';
import classNames from 'classnames';
import prependHttp from 'prepend-http';
import {
  ContentSchema,
  RoomJSONStructureContent,
} from '../../../../definitions/schemas';
import { formatMilliseconds } from '../../../../Services/Formaters';
import { getAspectRatioHeight } from '../../../../lib/Media';
import { getContentCover, getTextContentURL } from '../../../Content/helper';
import { ReactComponent as AudioDefaultCover } from '../../../../svg/default-cover/audio-default-cover.svg';
import { SimplifiedTrackingProps } from '../../../Rooms/Tracking/types';
import { TheaterModeContext } from '../TheaterMode/TheaterModeContext';
import ContentTextRenderer from '../../../ContentTextRenderer';
import ContentType from '../../../../enums/ContentType';
import EmbeddableCover from '../../../Embeddable/EmbeddableCover';
import RoomContentTileCover from './RoomContentTileCover';
import TextTracker from '../../../Rooms/Tracking/TextTracker';
import UrlTracker from '../../../Rooms/Tracking/UrlTracker';
import ContentStatusTag, {
  ContentStatusSizeType,
  ContentStatusType,
} from '../../../ContentStatusTag';
import BuyerContentAssetActions from './BuyerContentAssetActions';
import { useBuyerContentAssetActionsStyles } from './buyerContentAssetActionsStyles';

const useStyles = makeStyles((theme: Theme) => ({
  anchor: {
    display: 'block',
  },
  defaultCover: {
    height: '100%',
    width: '100%',
  },
  linkIcon: {
    height: theme.spacing(2),
    width: theme.spacing(2),
  },
  tag: {
    alignItems: 'center',
    backgroundColor: 'var(--content-tag)',
    borderRadius: theme.spacing(12.5),
    bottom: theme.spacing(1),
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    minWidth: theme.spacing(5),
    padding: theme.spacing(0, 1),
    position: 'absolute',
    right: theme.spacing(1),
    zIndex: 1,
  },
  tagIconOnly: {
    borderRadius: '50%',
    height: theme.spacing(3),
    minWidth: theme.spacing(3),
    padding: 0,
    width: theme.spacing(3),
  },
  textPreview: {
    backgroundColor: 'var(--background-box-dark)',
    height: '100%',
    overflow: 'hidden',
    padding: theme.spacing(3),
    position: 'relative',
    '&::after': {
      background:
        'linear-gradient(180deg, rgba(239, 241, 242, 0) 0%, #EFF1F2 100%)',
      bottom: 0,
      content: '""',
      display: 'block',
      height: '30%',
      left: 0,
      position: 'absolute',
      width: '100%',
    },
  },
  wrapper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    height: theme.spacing(17),
    overflow: 'hidden',
    position: 'relative',
  },
}));

type ClassKey = 'content';

export interface RoomContentTileProps extends SimplifiedTrackingProps {
  classes?: Partial<Record<ClassKey, string>>;
  content: Partial<ContentSchema>;
  structureContent?: RoomJSONStructureContent;
  width?: number | null;
  assetActionFlags?: { showCopilot: boolean; showCopy: boolean };
  onCopilotIconClicked?: () => void;
  onCopyAssetLink?: (sectionId: string) => void;
}

const RoomContentTile: React.FC<RoomContentTileProps> = (
  props: RoomContentTileProps
): JSX.Element => {
  const classes = useStyles();
  const assetActionStyles = useBuyerContentAssetActionsStyles();

  const { displayContentOnTheaterMode } = useContext(TheaterModeContext);

  const {
    assetActionFlags,
    classes: classesProp,
    content,
    onCopilotIconClicked,
    onCopyAssetLink,
    structureContent,
    width,
    ...trackingProps
  } = props;

  const height = useMemo(
    () => (!width ? undefined : getAspectRatioHeight(width)),
    [width]
  );

  const isTextContentUrl = useMemo(() => {
    const [, isUrl] = getTextContentURL(content.text?.text);
    return isUrl;
  }, [content]);

  const href = useMemo(() => {
    if (isTextContentUrl) {
      return content.text?.text;
    }

    if (content.url && !content.url.iframe) {
      return content.url.url;
    }

    return undefined;
  }, [content, isTextContentUrl]);

  const onTileClicked = () => {
    if (!href) {
      displayContentOnTheaterMode(content.id!);
    }
  };

  const renderDefaultCover = (): JSX.Element => {
    switch (content.type) {
      case ContentType.AUDIO:
        return <AudioDefaultCover className={classes.defaultCover} />;
      case ContentType.EMBEDDABLE:
        return (
          <EmbeddableCover name={content.name} url={content.embeddable?.url} />
        );
      default:
        return <RoomContentTileCover />;
    }
  };

  const renderTextPreview = (): JSX.Element => (
    <Box className={classes.textPreview}>
      <ContentTextRenderer
        disableAnchor
        disableAnchorIcon
        html={content.text?.html}
        text={content.text?.text || ''}
        whiteSpace="pre-wrap"
      />
    </Box>
  );

  const renderTextTracker = (children: React.ReactNode): React.ReactNode => (
    <TextTracker
      {...trackingProps}
      contentId={content.id!}
      text={content.text?.text}
      version={content.version}
    >
      {children}
    </TextTracker>
  );

  const renderURLTracker = (children: React.ReactNode) => {
    const [cover, defaultCover, coverFromStructure] = getContentCover(
      content,
      structureContent
    );

    return (
      <UrlTracker
        {...trackingProps}
        contentId={content.id!}
        cover={coverFromStructure || cover || defaultCover}
        description={content.description}
        iframe={content.url?.iframe}
        name={content.name!}
        openGraph={content.url?.openGraph!}
        url={content.url?.url!}
        version={content.version}
        content={content}
      >
        {children}
      </UrlTracker>
    );
  };

  const renderCover = (
    extendedChildren?: (cover: React.ReactNode) => JSX.Element
  ): React.ReactNode => {
    const [coverUrl, defaultCoverUrl, coverFromStructure] = getContentCover(
      content,
      structureContent
    );
    const coverSrc = coverFromStructure || coverUrl || defaultCoverUrl;

    /**
     * Text that are links (the only text they contain is a URL) must trigger
     * the click event immediately, but there are multiple scenarios: links,
     * plain texts, links with a cover and plain texts with a cover. So we only
     * wrap the texts that are links with their tracker
     */
    if (content.type === ContentType.TEXT) {
      const textCover = coverSrc ? (
        <RoomContentTileCover src={coverSrc} />
      ) : (
        renderTextPreview()
      );

      return isTextContentUrl
        ? renderTextTracker(
            extendedChildren ? extendedChildren(textCover) : textCover
          )
        : textCover;
    }

    const coverEl = coverSrc ? (
      <RoomContentTileCover src={coverSrc} />
    ) : (
      renderDefaultCover()
    );

    /**
     * URls that open in a new tab (no iframed) must trigger the click event
     * immediately when they are clicked. Since they follow the same logic as
     * other types of contents, we generate the cover and wrap it
     */
    if (content.type === ContentType.URL && !content.url?.iframe) {
      return renderURLTracker(
        extendedChildren ? extendedChildren(coverEl) : coverEl
      );
    }

    return coverEl;
  };

  const renderTag = (): React.ReactNode => {
    let tag: React.ReactNode;
    let iconOnly = false;

    const [cover, defaultCover, coverFromStructure] = getContentCover(
      content,
      structureContent
    );

    switch (content.type) {
      case ContentType.AUDIO:
        tag = formatMilliseconds((content.audio?.totalSeconds || 0) * 1000, 2);
        break;
      case ContentType.DOCUMENT:
        tag = 'DOC';
        break;
      case ContentType.IMAGE:
        tag = 'IMAGE';
        break;
      case ContentType.TEXT: {
        if (isTextContentUrl) {
          tag = <OpenInNew color="inherit" className={classes.linkIcon} />;
          iconOnly = true;
        } else if (coverFromStructure || cover || defaultCover) {
          tag = 'TEXT';
        }
        break;
      }
      case ContentType.URL: {
        if (!content.url?.iframe) {
          tag = <OpenInNew color="inherit" className={classes.linkIcon} />;
          iconOnly = true;
        }

        break;
      }
      case ContentType.VIDEO:
        tag = formatMilliseconds((content.video?.totalSeconds || 0) * 1000, 2);
        break;
    }

    if (!tag) {
      return undefined;
    }

    return (
      <Box
        className={classNames(classes.tag, { [classes.tagIconOnly]: iconOnly })}
      >
        <Typography color="inherit" variant="caption">
          <Box component="strong" display="flex">
            {tag}
          </Box>
        </Typography>
      </Box>
    );
  };

  const renderStatusTag = (): React.ReactNode => {
    if (trackingProps?.track || !content.expired) {
      return;
    }

    return (
      <ContentStatusTag
        size={ContentStatusSizeType.Default}
        statusType={ContentStatusType.Expired}
        sx={(theme) => ({
          left: theme.spacing(1),
          position: 'absolute',
          top: theme.spacing(1.5),
        })}
      />
    );
  };

  const showCopilot = assetActionFlags?.showCopilot ?? false;
  const showCopy = assetActionFlags?.showCopy ?? false;
  const hasTileActions = showCopilot || showCopy;
  const deepLinkTileHover = hasTileActions;
  const sectionId = String(structureContent?.id ?? content.id);

  const wrapperClassName = classNames(classes.wrapper, classesProp?.content, {
    [assetActionStyles.tileHover]: deepLinkTileHover,
  });

  const tileActions = (
    <BuyerContentAssetActions
      onCopilotClick={onCopilotIconClicked}
      onCopyLink={onCopyAssetLink}
      sectionId={sectionId}
      showCopilot={showCopilot}
      showCopy={showCopy}
      variant="tile"
    />
  );

  if (href) {
    // special case for URLs that open in a new tab to trigger the click event for tracking
    // render anchor tag with the cover and tag instead of the cover itself
    const renderUrlComponent = (cover: React.ReactNode) => (
      <a
        className={classNames(classes.anchor, wrapperClassName)}
        href={prependHttp(href)}
        rel="noopener noreferrer"
        style={{ height, position: 'relative' }}
        target="_blank"
        aria-label={`Open ${content.type || 'content'} titled ${
          content.name || `item ${content.id}`
        } in new tab`}
      >
        {tileActions}
        {cover}
        {renderTag()}
      </a>
    );

    return <>{renderCover(renderUrlComponent)}</>;
  }

  return (
    <Box
      className={wrapperClassName}
      onClick={onTileClicked}
      role="button"
      aria-label={`Open ${content.type || 'content'} titled ${
        content.name || `item ${content.id}`
      } in theater mode`}
      style={{ height }}
      tabIndex={0}
    >
      {tileActions}
      {renderCover()}
      {renderTag()}
      {renderStatusTag()}
    </Box>
  );
};

export default RoomContentTile;
